import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { AdminEngagementContext } from './AdminEngagementContext';
import { reducer } from './reducer';
import {
  EngagementType,
  FullEngagementFragment,
  FullEventFragment,
  TriviaAdminConfig,
  UpdateEngagementInput,
  useUpdateEngagementMutation,
} from '@/generated/graphql';
import { debounce, isEqual } from 'lodash';
import { createLogger } from '@/utils/createLogger';
import {
  applyServerEngagementPush,
  fullEngagementToUpdateInput,
  shouldQueueAutosave,
} from './updateInput';
import { serverEngagementReceived } from './actions';
import { AdminEngagementState } from './reducer';
import {
  applyDraftToEditor,
  clearTriviaEditorDraft,
  draftFromEditorState,
  loadTriviaEditorDraft,
  saveTriviaEditorDraft,
  TriviaEditorDraft,
} from '@/engagements/trivia/admin/editorDraft';
import { firstTriviaSaveErrorMessage } from '@/engagements/trivia/admin/questionValidation';
import {
  EditorSaveStatus,
  graphqlErrorMessage,
  saveStatusFromMutationResult,
} from '@/engagements/trivia/admin/saveStatus';

const logger = createLogger('AdminEngagementProvider');

function persistTriviaDraft(
  snapshot: AdminEngagementState,
  saveError: string | null,
) {
  if (snapshot.type !== EngagementType.Trivia) return;
  saveTriviaEditorDraft(
    draftFromEditorState({
      engagementId: snapshot.id,
      title: snapshot.title,
      description: snapshot.description,
      qrCodeCta: snapshot.qrCodeCta,
      config:
        snapshot.config as TriviaAdminConfig as TriviaEditorDraft['config'],
      saveError,
    }),
  );
}

export const AdminEngagementProvider = ({
  children,
  engagement,
  event,
}: {
  children: React.ReactNode;
  event: FullEventFragment;
  engagement: FullEngagementFragment;
}) => {
  const [state, dispatch] = useReducer(reducer, {
    ...engagement,
    editorMode: 'stage',
  });
  const [lastSaved, setLastSaved] = useState<AdminEngagementState>(() => ({
    ...engagement,
    editorMode: 'stage',
  }));
  const [saveStatus, setSaveStatus] = useState<EditorSaveStatus>(() => {
    if (engagement.type !== EngagementType.Trivia) return { kind: 'saved' };
    const message = firstTriviaSaveErrorMessage(
      engagement.config as TriviaAdminConfig,
    );
    return message ? { kind: 'error', message } : { kind: 'saved' };
  });
  const [updateEngagement] = useUpdateEngagementMutation({
    errorPolicy: 'all',
  });
  const restoredForId = useRef<string | null>(null);

  const debouncedOnSave = useMemo(
    () =>
      debounce(
        (input: UpdateEngagementInput, snapshot: AdminEngagementState) => {
          logger.log('debouncedOnSave: ', {
            input,
          });
          setSaveStatus({ kind: 'saving' });
          updateEngagement({
            variables: {
              engagementId: engagement.id,
              data: input,
            },
            errorPolicy: 'all',
          })
            .then((result) => {
              const next = saveStatusFromMutationResult(result);
              if (next.kind === 'error') {
                persistTriviaDraft(snapshot, next.message);
                setSaveStatus(next);
                return;
              }
              if (snapshot.type === EngagementType.Trivia) {
                const localMessage = firstTriviaSaveErrorMessage(
                  snapshot.config as TriviaAdminConfig,
                );
                if (localMessage) {
                  persistTriviaDraft(snapshot, localMessage);
                  setSaveStatus({ kind: 'error', message: localMessage });
                  return;
                }
              }
              setLastSaved(snapshot);
              clearTriviaEditorDraft(snapshot.id);
              setSaveStatus({ kind: 'saved' });
            })
            .catch((error: unknown) => {
              const message = graphqlErrorMessage(error);
              persistTriviaDraft(snapshot, message);
              setSaveStatus({ kind: 'error', message });
            });
        },
        500,
      ),
    [engagement.id, updateEngagement],
  );

  useEffect(() => {
    if (engagement.type !== EngagementType.Trivia) return;
    if (restoredForId.current === engagement.id) return;
    restoredForId.current = engagement.id;
    const draft = loadTriviaEditorDraft(engagement.id);
    if (!draft) return;
    const baseline: AdminEngagementState = {
      ...engagement,
      editorMode: 'stage',
    };
    const next = applyDraftToEditor(baseline, draft) as AdminEngagementState;
    setLastSaved(baseline);
    dispatch(serverEngagementReceived(next));
    setSaveStatus(
      draft.saveError
        ? { kind: 'error', message: draft.saveError }
        : { kind: 'error', message: 'Unsaved changes' },
    );
  }, [engagement]);

  useEffect(() => {
    const { nextEditor, nextLastSaved, didChange } = applyServerEngagementPush({
      editor: state,
      lastSaved,
      incoming: engagement,
    });
    if (!didChange || isEqual(nextEditor, state)) {
      return;
    }
    setLastSaved(nextLastSaved as AdminEngagementState);
    dispatch(serverEngagementReceived(nextEditor as AdminEngagementState));
    // Editor `state` is intentionally omitted: this effect only folds Apollo /
    // subscription props in. User edits go through the auto-save effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagement]);

  useEffect(() => {
    if (!shouldQueueAutosave(state, lastSaved)) {
      debouncedOnSave.cancel();
      return;
    }
    setSaveStatus({ kind: 'saving' });
    persistTriviaDraft(state, null);
    debouncedOnSave(
      fullEngagementToUpdateInput(state, lastSaved) as UpdateEngagementInput,
      state,
    );
  }, [state, lastSaved, debouncedOnSave]);

  return (
    <AdminEngagementContext.Provider
      value={{
        state,
        dispatch,
        event,
        saveStatus,
      }}
    >
      {children}
    </AdminEngagementContext.Provider>
  );
};
